/**
 * Las analíticas de una partida (§11).
 *
 * Puras y aquí, porque son reglas de dominio y porque la prueba de que la tasa de acierto es la que
 * se puede contar a mano de sus respuestas tiene que poder escribirse sin base de datos.
 *
 * **El dato más valioso de todo el panel no es la clasificación**, y lo dice el propio documento
 * maestro: es el **distractor eficaz**, la opción incorrecta que elige más del 40 % de la clase. Una
 * clasificación dice quién ganó; un distractor eficaz dice qué hay que volver a explicar.
 */
/**
 * Por encima de esta proporción, una opción incorrecta deja de ser un despiste repartido y pasa a
 * ser un error conceptual que comparte la clase (§11).
 */
export const DISTRACTOR_THRESHOLD = 0.4;
/**
 * Por debajo de estas respuestas no se señala ningún distractor.
 *
 * Con tres respuestas, un «66 %» son dos personas. Señalarlo convertiría el dato más valioso del
 * panel en ruido; lo que se pierde a cambio es señalarlo en una clase de cuatro.
 */
export const MIN_SAMPLE = 5;
function average(values) {
    if (values.length === 0)
        return null;
    return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}
export function reportQuestion(question, answers) {
    const suyas = answers.filter((answer) => answer.questionId === question.questionId);
    const respondidas = suyas.filter((answer) => answer.optionId !== null);
    const aciertos = respondidas.filter((answer) => answer.isCorrect).length;
    const options = question.options.map((option) => {
        const count = respondidas.filter((answer) => answer.optionId === option.id).length;
        const share = respondidas.length === 0 ? 0 : count / respondidas.length;
        return {
            optionId: option.id,
            textMd: option.textMd,
            correct: option.correct,
            count,
            share,
            // **Nunca la correcta**, por muy elegida que sea: un distractor es, por definición, un error
            // compartido. Y nunca sobre una muestra que no significa nada.
            distractor: !option.correct && respondidas.length >= MIN_SAMPLE && share > DISTRACTOR_THRESHOLD,
        };
    });
    return {
        questionId: question.questionId,
        position: question.position,
        promptMd: question.promptMd,
        answered: respondidas.length,
        correct: aciertos,
        // Nulo, no cero: «nadie respondió» y «todos fallaron» son cosas distintas, y confundirlas hace
        // que una pregunta que no dio tiempo a jugar parezca la peor del cuestionario.
        accuracy: respondidas.length === 0 ? null : aciertos / respondidas.length,
        averageMs: average(respondidas.map((answer) => answer.elapsedMs)),
        options,
        hasDistractor: options.some((option) => option.distractor),
    };
}
export function reportParticipant(participant, questions, answers) {
    const suyas = answers.filter((answer) => answer.participantId === participant.participantId);
    const respondidas = suyas.filter((answer) => answer.optionId !== null);
    return {
        participantId: participant.participantId,
        displayName: participant.displayName,
        alias: participant.alias,
        correct: respondidas.filter((answer) => answer.isCorrect).length,
        answered: respondidas.length,
        unanswered: Math.max(0, questions.length - respondidas.length),
        points: participant.score,
        averageMs: average(respondidas.map((answer) => answer.elapsedMs)),
    };
}
export function buildReport(questions, participants, answers) {
    const ordered = [...questions].sort((a, b) => a.position - b.position);
    const reports = ordered.map((question) => reportQuestion(question, answers));
    // Solo se comparan las que alguien respondió: destacar como «peor resuelta» una que nadie llegó a
    // jugar sería exactamente al revés de lo que el panel quiere decir.
    const comparables = reports.filter((report) => report.accuracy !== null);
    const best = comparables.reduce((mejor, actual) => mejor === null || (actual.accuracy ?? 0) > (mejor.accuracy ?? 0) ? actual : mejor, null);
    const worst = comparables.reduce((peor, actual) => peor === null || (actual.accuracy ?? 1) < (peor.accuracy ?? 1) ? actual : peor, null);
    const respondidas = answers.filter((answer) => answer.optionId !== null);
    return {
        questions: reports,
        // Quien fue expulsado no aparece: ya no estaba en la partida.
        participants: participants
            .filter((participant) => participant.connection !== 'kicked')
            .map((participant) => reportParticipant(participant, ordered, answers)),
        // Con una sola pregunta comparable, «la mejor» y «la peor» son la misma y no dicen nada.
        bestQuestionId: comparables.length > 1 ? (best?.questionId ?? null) : null,
        worstQuestionId: comparables.length > 1 ? (worst?.questionId ?? null) : null,
        totalAnswers: respondidas.length,
        overallAccuracy: respondidas.length === 0
            ? null
            : respondidas.filter((answer) => answer.isCorrect).length / respondidas.length,
    };
}
//# sourceMappingURL=report.js.map